import React, { useState, useEffect } from 'react';
import { Mail, Clock, CheckCircle, XCircle, Trash2, RefreshCw, X } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { invitationService } from '../../../services/invitationService';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';


export default function InvitationListModal({ isOpen, onClose, tenantId }) {
  const { profile } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadInvitations();
    }
  }, [isOpen, tenantId]);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await invitationService?.getInvitationsForTenant(tenantId || profile?.tenant_id);
      setInvitations(data);
    } catch (err) {
      setError(err?.message || 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (invitationId) => {
    try {
      await invitationService?.cancelInvitation(invitationId);
      await loadInvitations();
    } catch (err) {
      setError(err?.message || 'Failed to cancel invitation');
    }
  };

  const handleResend = async (invitationId) => {
    try {
      await invitationService?.resendInvitation(invitationId);
      await loadInvitations();
    } catch (err) {
      setError(err?.message || 'Failed to resend invitation');
    }
  };

  const handleDelete = async (invitationId) => {
    if (!confirm('Are you sure you want to delete this invitation?')) return;
    
    try {
      await invitationService?.deleteInvitation(invitationId);
      await loadInvitations();
    } catch (err) {
      setError(err?.message || 'Failed to delete invitation');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { bg: 'bg-warning/10', text: 'text-warning', icon: Clock, label: 'Pending' },
      accepted: { bg: 'bg-success/10', text: 'text-success', icon: CheckCircle, label: 'Accepted' },
      declined: { bg: 'bg-error/10', text: 'text-error', icon: XCircle, label: 'Declined' },
      expired: { bg: 'bg-gray-100', text: 'text-gray-600', icon: Clock, label: 'Expired' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-600', icon: XCircle, label: 'Cancelled' }
    };

    const { bg, text, icon: Icon, label } = config?.[status] || config?.pending;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>
        <Icon className="w-3 h-3" />
        {label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Pending Invitations</h2>
            <p className="text-sm text-gray-500 mt-1">
              {invitations?.length} {invitations?.length === 1 ? 'invitation' : 'invitations'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-error">{error}</p>
              <Button onClick={loadInvitations} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : invitations?.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No invitations found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invitations?.map((invitation) => (
                <div
                  key={invitation?.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Mail className="w-5 h-5 text-gray-400" />
                        <span className="font-medium text-gray-900">{invitation?.email}</span>
                        {getStatusBadge(invitation?.status)}
                      </div>
                      <div className="ml-8 space-y-1 text-sm text-gray-600">
                        <p>
                          <span className="font-medium">Role:</span> {invitation?.role?.displayName}
                        </p>
                        <p>
                          <span className="font-medium">Invited by:</span>{' '}
                          {invitation?.inviter?.first_name} {invitation?.inviter?.last_name}
                        </p>
                        <p>
                          <span className="font-medium">Sent:</span> {formatDate(invitation?.createdAt)}
                        </p>
                        {invitation?.status === 'pending' && (
                          <p>
                            <span className="font-medium">Expires:</span> {formatDate(invitation?.expiresAt)}
                          </p>
                        )}
                        {invitation?.message && (
                          <p className="mt-2 italic text-gray-500">"{invitation?.message}"</p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    {invitation?.status === 'pending' && (
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleResend(invitation?.id)}
                          className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                          title="Resend invitation"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleCancel(invitation?.id)}
                          className="p-2 text-warning hover:bg-warning/10 rounded-lg transition-colors"
                          title="Cancel invitation"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(invitation?.id)}
                          className="p-2 text-error hover:bg-error/10 rounded-lg transition-colors"
                          title="Delete invitation"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}